/**
 * protoToChatHistory — converts shanks AgentService.GetConversation
 * responses into the ChatMessage shape ChatView consumes.
 *
 * Output contract (must hold for both flag-on and flag-off paths so
 * ChatView renders identically):
 *   1. role='tool' messages are dropped entirely (legacy tool-as-
 *      separate-message format; tool data now lives in parts on the
 *      assistant message that invoked it).
 *   2. Messages with a parts ListValue use it. Parts are walked in
 *      stored order; each `tool-call` is merged with its matching
 *      `tool-result` (same toolCallId) into a single `tool-${name}`
 *      part with the result under `output`. Standalone `tool-result`
 *      parts are dropped (they're carried by the merged tool-call).
 *      Text parts pass through unchanged, as do `data-artifact` parts
 *      (the saved-artifact reference LogArtifactToChat writes — these
 *      are what let a saved artifact survive a page reload).
 *   3. Messages with no parts but a non-empty `content` (legacy pre-
 *      parts rows) emit a single `{type:'text', text: content}` part.
 *   4. Messages with neither parts nor content emit `parts: []`.
 *   5. createdAt round-trips through Timestamp.toDate(); missing
 *      timestamps fall back to "now" rather than throwing.
 *
 * Tests in protoToChatHistory.test.ts pin every clause above.
 *
 * PR 6 will delete the express counterpart that used to define this
 * contract (services/express-backend/src/routes/Chat/getChat); the
 * tests in this directory then become the sole spec.
 */ /**
 * Convert a GetConversationResponse into the {messages, projectId} shape
 * Chat.tsx already passes to ChatView. Returns empty messages for a nil
 * conversation (matches the express empty-response branch for missing
 * shanks rows).
 */ export function convertProtoConversation(conversation, messages) {
    if (!conversation) {
        return {
            messages: [],
            projectId: undefined
        };
    }
    const formatted = [];
    for (const m of messages){
        if (m.role === 'tool') continue;
        formatted.push(convertMessage(m));
    }
    return {
        messages: formatted,
        projectId: conversation.projectId || undefined
    };
}
function convertMessage(m) {
    const partsList = readPartsList(m);
    let parts;
    if (partsList) {
        parts = mergeToolPairs(partsList);
    } else if (m.content) {
        parts = [
            {
                type: 'text',
                text: m.content
            }
        ];
    } else {
        parts = [];
    }
    // Shanks keeps attachment tool instructions in the saved model context.
    // Reopened user bubbles should show the selected filenames and request.
    if (m.role === 'user') {
        const hasAttachments = parts.some((part)=>part.type === 'data-attachments');
        parts = parts.flatMap((part)=>part.type === 'text' ? attachmentDisplayParts(part.text, hasAttachments) : [
                part
            ]);
    }
    const createdAt = m.createdAt ? m.createdAt.toDate().toISOString() : new Date().toISOString();
    return {
        id: m.id,
        role: m.role,
        parts,
        metadata: {
            createdAt
        }
    };
}
function attachmentDisplayParts(text, hasAttachments) {
    const unchanged = [
        {
            type: 'text',
            text
        }
    ];
    const prefix = '[ATTACHED FILES]\nThe user has attached the following files to this conversation:\n';
    const instructions = "\n\nThe 'read-document-chunks' tool is already available to you. Use it to read file content:\n";
    const end = '- Search content: {"file_id": "<ID>", "search_query": "keyword"}\n\n';
    if (!text.startsWith(prefix)) return unchanged;
    const instructionStart = text.indexOf(instructions, prefix.length);
    const contextEnd = text.indexOf(end, instructionStart);
    if (instructionStart < 0 || contextEnd < 0) return unchanged;
    const attachments = text.slice(prefix.length, instructionStart).split('\n').map((line)=>{
        const match = /^- (.+) \(ID: ([a-zA-Z0-9_-]+), \d+ words, \d+ chunks\)$/.exec(line);
        if (!match) return undefined;
        const extension = match[1].split('.').pop()?.toLowerCase() ?? '';
        const mimeTypes = {
            pdf: 'application/pdf',
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        };
        return {
            id: match[2],
            name: match[1],
            contentType: mimeTypes[extension] ?? 'application/octet-stream'
        };
    });
    if (attachments.length === 0 || attachments.some((file)=>!file)) return unchanged;
    const files = attachments.filter((file)=>file !== undefined);
    const parts = [
        {
            type: 'text',
            text: `Attached: ${files.map((file)=>file.name).join(', ')}\n\n${text.slice(contextEnd + end.length)}`
        }
    ];
    if (!hasAttachments) parts.push({
        type: 'data-attachments',
        data: files
    });
    return parts;
}
/**
 * Pull the parts array off a proto Message, normalizing the ListValue
 * shape (`{values: [{kind: structValue, structValue: {...}}]}`) into a
 * plain array of plain JS objects. Returns null when no parts are
 * present so the caller can fall back to rendering `content`.
 */ function readPartsList(m) {
    const lv = m.parts;
    if (!lv) return null;
    const values = lv.values;
    if (!values || values.length === 0) return null;
    const out = [];
    for (const v of values){
        const obj = v.toJson();
        if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
            out.push(obj);
        }
    }
    return out;
}
/**
 * Walk parts in stored order, merging each tool-call with its
 * corresponding tool-result (matched by toolCallId) into a single
 * `tool-${name}` part with the result under `output`. Standalone
 * tool-result parts are dropped (the merged tool-call carries them).
 *
 * Two-pass: the first pass builds toolCallId → result map so the
 * emit pass can splice results into their tool-call slot in O(1)
 * regardless of stored ordering. State defaults to "output-available"
 * to match the consumer's expectation that a persisted tool-call
 * with no explicit state is render-ready.
 */ function mergeToolPairs(stored) {
    const toolResults = new Map();
    for (const p of stored){
        if (p.type === 'tool-result' && p.toolCallId) {
            toolResults.set(p.toolCallId, p);
        }
    }
    const out = [];
    for (const p of stored){
        if (p.type === 'text') {
            out.push({
                type: 'text',
                text: p.text || ''
            });
        } else if (p.type === 'data-artifact' || p.type === 'data-attachments') {
            // The saved-artifact reference CollabService.LogArtifactToChat writes.
            // Pass it through verbatim: ChatView reads it back into the artifact store
            // and MessagePartRenderer renders it as the clickable preview card that
            // opens the drawer. Drop it and a saved artifact is unreachable from its
            // own chat the moment the page reloads.
            out.push({
                type: p.type,
                data: p.data
            });
        } else if (p.type === 'tool-call') {
            const result = p.toolCallId ? toolResults.get(p.toolCallId) : undefined;
            out.push({
                type: `tool-${p.toolName}`,
                toolName: p.toolName,
                toolCallId: p.toolCallId,
                state: result ? 'output-available' : 'input-available',
                input: p.args,
                purpose: p.purpose,
                executionState: p.executionState,
                duration: result?.durationMs,
                output: result?.result
            });
        }
    // tool-result parts are merged into tool-call above; skip
    }
    return out;
}
